// Các popup nghiệp vụ thẻ liệu trình: bán thẻ, thu tiền đợt, trừ buổi, đặt lịch, gia hạn, điều chỉnh, hoàn tiền, sửa lịch thanh toán
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, DatePicker, Input, InputNumber, Modal, Radio, Segmented, Select, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { AlertTriangle, KeyRound, Minus, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { BRANCHES, METHOD_LABEL, METHODS, METHODS_NEED_REF, STAFF, type Customer, type PaymentMethod } from '../POS/data';
import usePosStore from '../POS/store';
import { usePosAuth } from '../POS/utils';
import CustomerPicker from '../POS/components/CustomerPicker';
import MoneyInput from '../POS/components/MoneyInput';
import { KIND_LABEL, ROLE_LABEL, TREATMENT_SERVICES, pricingOf, serviceOf, type PayRequest, type CardBooking, type Installment, type TreatmentCard, type TreatmentPackage } from './data';
import {
  addDaysYmd, addMonthsYmd, awaitingActivation, buildPlan, cardBalance, cardPolicy, checkUsage, discountCap, expiryText, extensionQuote, installmentRows, lineBalances,
  monthsBetween, outstanding, refundQuote, round1000, validityText, ymd,
  type UsageRequestItem,
} from './logic';
import useTreatmentStore from './store';
import { payCard, posDebtOf, quoteSale, refundCard, sellCard, useTreatmentAuth } from './actions';
import { fmtDate, money, moneyShort } from './ui';
import PaidModal, { type PaidResult } from './PaidModal';
import SignaturePad from '../Customer360/SignaturePad';
import { signVisit, todayYmd } from '../Customer360/visits';

const PAY_METHODS = METHODS.filter(m => m !== 'prepaid');
const REF_RE = /^[A-Za-z0-9]{4,50}$/;
const branchOptions = BRANCHES.map(b => ({ value: b.id, label: b.name }));
const staffOptions = (branchId?: string) => STAFF.filter(s => !branchId || s.branchId === branchId).map(s => ({ value: s.id, label: s.name }));

const Field = ({ label, required, hint, children, className = '' }: { label: React.ReactNode; required?: boolean; hint?: React.ReactNode; children: React.ReactNode; className?: string }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
    {hint && <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>}
  </label>
);

/** Phương thức + mã giao dịch (bắt buộc với chuyển khoản / thẻ / ví) */
function MethodPicker({ method, txnRef, onChange }: { method: PaymentMethod; txnRef: string; onChange: (m: PaymentMethod, ref: string) => void }) {
  const needRef = METHODS_NEED_REF.includes(method);
  return (
    <div className="space-y-2">
      <Segmented<PaymentMethod> block value={method} onChange={m => onChange(m, txnRef)} options={PAY_METHODS.map(m => ({ value: m, label: METHOD_LABEL[m] }))} />
      {needRef && (
        <Input value={txnRef} maxLength={50} placeholder="Mã giao dịch (VD: FT26102000123)" status={txnRef && !REF_RE.test(txnRef) ? 'error' : undefined}
          onChange={e => onChange(method, e.target.value.toUpperCase().replace(/\s/g, ''))} />
      )}
    </div>
  );
}
const methodValid = (m: PaymentMethod, ref: string) => !METHODS_NEED_REF.includes(m) || REF_RE.test(ref);

/** Bảng lịch thanh toán sửa được: ngày đến hạn + số tiền từng đợt */
function PlanEditor({ plan, onChange, lockedNos = [], minDate }: { plan: Installment[]; onChange: (p: Installment[]) => void; lockedNos?: number[]; minDate?: string }) {
  const set = (i: number, patch: Partial<Installment>) => onChange(plan.map((p, k) => (k === i ? { ...p, ...patch } : p)));
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500">
          <tr><th className="px-2 py-1.5 text-left">Đợt</th><th className="px-2 py-1.5 text-left">Ngày đến hạn</th><th className="px-2 py-1.5 text-right">Số tiền</th><th className="w-8" /></tr>
        </thead>
        <tbody>
          {plan.map((p, i) => {
            const locked = lockedNos.includes(p.no);
            return (
              <tr key={p.no} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-semibold">{p.no}</td>
                <td className="px-2 py-1.5">
                  <DatePicker size="small" format="DD/MM/YYYY" allowClear={false} disabled={locked} value={dayjs(p.dueDate)}
                    disabledDate={d => !!minDate && d.isBefore(dayjs(minDate), 'day')}
                    onChange={(d: Dayjs | null) => d && set(i, { dueDate: d.format('YYYY-MM-DD') })} />
                </td>
                <td className="px-2 py-1.5"><MoneyInput value={p.amount} disabled={locked} onChange={v => set(i, { amount: v })} className="!h-8 !text-sm" /></td>
                <td className="px-1">
                  {!locked && plan.length > 1 && (
                    <button type="button" onClick={() => onChange(plan.filter((_, k) => k !== i).map((x, k) => ({ ...x, no: k + 1 })))} className="p-1 text-slate-400 hover:text-red-500" aria-label="Xóa đợt">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bán thẻ

interface SellProps { open: boolean; onClose: () => void; customer?: Customer | null; onDone?: (card: TreatmentCard) => void }

export function SellCardModal({ open, onClose, customer: initialCustomer, onDone }: SellProps) {
  const packages = useTreatmentStore(s => s.packages);
  const cfg = useTreatmentStore(s => s.config);
  const orders = usePosStore(s => s.orders);
  const posConfig = usePosStore(s => s.config);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { role } = usePosAuth();
  const active = packages.filter(p => p.active);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [packageId, setPackageId] = useState<string>('');
  const [branchId, setBranchId] = useState(workingBranch);
  const [consultantId, setConsultantId] = useState('');
  const [discountType, setDiscountType] = useState<'%' | 'đ'>('%');
  const [discountValue, setDiscountValue] = useState(0);
  const [count, setCount] = useState(1);
  const [first, setFirst] = useState(0);
  const [plan, setPlan] = useState<Installment[]>([]);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [txnRef, setTxnRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paid, setPaid] = useState<PaidResult | null>(null);

  useEffect(() => {
    if (!open) return;
    setCustomer(initialCustomer || null);
    setPackageId(active[0]?.id || '');
    setBranchId(workingBranch);
    setConsultantId('');
    setDiscountType('%'); setDiscountValue(0); setCount(1); setMethod('cash'); setTxnRef(''); setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pkg = packages.find(p => p.id === packageId);
  const quote = useMemo(() => (packageId ? quoteSale({ packageId, branchId, discountType, discountValue, consultantId, installments: count }) : null), [packageId, branchId, discountType, discountValue, consultantId, count]);
  const price = quote?.pricing.totals.total || 0;
  const minFirst = pkg ? (count === 1 ? price : round1000(Math.ceil(price * pkg.minFirstPct / 100))) : 0;
  const sale = quote?.sale;
  const salePrice = sale?.listPrice || 0;
  const discountPct = salePrice > 0 ? (discountType === '%' ? discountValue : discountValue / salePrice * 100) : 0;
  const roleLimit = posConfig.discountLimit[role];
  const limit = pkg ? discountCap(pkg, roleLimit) : roleLimit;
  const interval = pkg ? pricingOf(pkg).intervalDays : cfg.defaultIntervalDays;
  const today = ymd();

  // Đổi gói / số đợt / giá thì dựng lại lịch
  useEffect(() => {
    if (!pkg) return;
    const c = Math.min(count, pkg.maxInstallments);
    if (c !== count) { setCount(c); return; }
    const f = c === 1 ? price : minFirst;
    setFirst(f);
    setPlan(buildPlan(price, c, f, today, interval));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId, count, price]);

  const changeFirst = (v: number) => { setFirst(v); setPlan(buildPlan(price, count, v, today, interval)); };
  const planTotal = plan.reduce((s, p) => s + p.amount, 0);
  const debtOf = (cid: string) => orders.filter(o => o.customerId === cid && o.status !== 'cancelled').reduce((s, o) => s + o.debt, 0);

  const errors: string[] = [];
  if (!customer) errors.push('Chọn khách hàng');
  if (!pkg) errors.push('Chọn gói');
  if (discountPct > limit + 1e-9) errors.push(limit < roleLimit ? `Gói này chỉ cho chiết khấu tối đa ${limit}%` : `Chiết khấu ${discountPct.toFixed(1)}% vượt hạn mức ${limit}% của bạn`);
  if (count > 1 && first < minFirst) errors.push(`Đợt 1 tối thiểu ${money(minFirst)}đ (${pkg?.minFirstPct}% giá thẻ)`);
  if (planTotal !== price) errors.push(`Tổng các đợt ${money(planTotal)}đ phải bằng giá thẻ ${money(price)}đ`);
  if (plan[0] && plan[0].amount !== first) errors.push('Số tiền đợt 1 phải bằng số thu ngay');
  if (first > 0 && !methodValid(method, txnRef)) errors.push('Nhập mã giao dịch hợp lệ');

  const submit = async () => {
    if (errors.length || !customer || !pkg) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 300));
    try {
      const { card, orderId } = sellCard({
        customer, packageId, branchId, discountType, discountValue, consultantId, plan,
        first: { method, amount: first, txnRef }, note: '',
      });
      message.success(`Đã phát hành thẻ ${card?.id} (đơn ${orderId})`);
      onClose();
      if (card) {
        onDone?.(card);
        setPaid({ cardId: card.id, paymentId: card.payments[0]?.id || null, change: 0, tendered: 0, orderId, title: `Đã phát hành thẻ ${card.id}` });
      }
    } catch (e) {
      message.error((e as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <>
    <PaidModal result={paid} onClose={() => setPaid(null)} />
    <Modal open={open} onCancel={onClose} title="Bán thẻ liệu trình" width={920} centered destroyOnHidden okText={`Thu ${money(first)}đ & phát hành thẻ`} cancelText="Hủy"
      onOk={submit} okButtonProps={{ disabled: errors.length > 0, loading: submitting }}>
      <div className="grid gap-5 md:grid-cols-[1fr_300px]">
        <div className="space-y-3">
          <Field label="Khách hàng" required>
            <CustomerPicker value={customer} onChange={setCustomer} debtOf={debtOf} />
          </Field>
          <Field label="Gói liệu trình" required>
            <Select className="w-full" value={packageId || undefined} onChange={setPackageId} showSearch optionFilterProp="label"
              options={active.map(p => ({ value: p.id, label: `${p.name} · ${money(p.price)}đ` }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Chi nhánh bán"><Select className="w-full" value={branchId} onChange={v => { setBranchId(v); setConsultantId(''); }} options={branchOptions} /></Field>
            <Field label="Tư vấn viên"><Select className="w-full" allowClear value={consultantId || undefined} onChange={v => setConsultantId(v || '')} options={staffOptions(branchId)} placeholder="Chọn" /></Field>
          </div>
          <Field label="Chiết khấu" hint={limit < roleLimit ? `Gói giới hạn chiết khấu tối đa ${limit}% (hạn mức của bạn ${roleLimit}%)` : `Hạn mức của bạn: ${limit}%`}>
            <div className="flex gap-2">
              <Segmented value={discountType} onChange={v => { setDiscountType(v as '%' | 'đ'); setDiscountValue(0); }} options={['%', 'đ']} />
              {discountType === '%'
                ? <InputNumber className="flex-1" min={0} max={100} value={discountValue} onChange={v => setDiscountValue(Number(v) || 0)} addonAfter="%" />
                : <MoneyInput value={discountValue} onChange={setDiscountValue} max={pkg?.price} className="!h-8 !text-sm" />}
            </div>
          </Field>
          <Field label="Thanh toán" hint={pkg && pkg.maxInstallments > 1 ? `Gói cho trả tối đa ${pkg.maxInstallments} đợt, đợt 1 tối thiểu ${pkg.minFirstPct}%, mỗi đợt cách ${interval} ngày${pricingOf(pkg).installmentSurchargePct ? `, phụ thu trả góp ${pricingOf(pkg).installmentSurchargePct}%` : ''}` : 'Gói này thanh toán một lần'}>
            <Segmented block value={count} onChange={v => setCount(Number(v))} disabled={!pkg || pkg.maxInstallments <= 1}
              options={Array.from({ length: pkg?.maxInstallments || 1 }, (_, i) => ({ value: i + 1, label: i === 0 ? 'Trả 1 lần' : `${i + 1} đợt` }))} />
          </Field>
          {count > 1 && (
            <>
              <Field label="Thu ngay (đợt 1)" required><MoneyInput value={first} onChange={changeFirst} max={price} invalid={first < minFirst} /></Field>
              <Field label="Lịch thanh toán" hint="Sửa ngày / số tiền từng đợt nếu khách hẹn khác lịch mặc định">
                <PlanEditor plan={plan} onChange={p => { setPlan(p); if (p[0]) setFirst(p[0].amount); }} lockedNos={[1]} minDate={today} />
              </Field>
            </>
          )}
          <Field label={`Phương thức thu ${money(first)}đ`}>
            <MethodPicker method={method} txnRef={txnRef} onChange={(m, r) => { setMethod(m); setTxnRef(r); }} />
          </Field>
        </div>

        <aside className="space-y-3 rounded-xl bg-slate-50 p-4 text-sm">
          {pkg ? <>
            <div>
              <div className="text-xs uppercase text-slate-500">{KIND_LABEL[pkg.kind]} · {pkg.category}</div>
              <div className="text-base font-bold">{pkg.name}</div>
              <div className="text-xs text-slate-500">Hạn dùng {validityText(pkg)}{(pkg.validityBasis || 'purchase') === 'purchase' && ` – đến ${fmtDate(addMonthsYmd(today, pkg.validityMonths))}`}</div>
            </div>
            {pkg.kind === 'session' ? (
              <ul className="space-y-1">
                {pkg.items.map(i => (
                  <li key={i.serviceId} className="flex justify-between gap-2">
                    <span>{serviceOf(i.serviceId)?.name} {i.role === 'bonus' && <span className="text-xs text-green-600">(tặng)</span>}</span>
                    <b>{i.sessions} buổi</b>
                  </li>
                ))}
              </ul>
            ) : <div>Mệnh giá sử dụng <b>{money(pkg.faceValue)}đ</b>{pkg.items.length ? ` · ${pkg.items.length} dịch vụ áp dụng` : ' · mọi dịch vụ'}</div>}
            <div className="space-y-1 border-t border-slate-200 pt-3">
              <div className="flex justify-between"><span>Giá niêm yết</span><span>{money(pkg.price)}đ</span></div>
              {sale && sale.source === 'branch' && <div className="flex justify-between"><span>Giá tại chi nhánh</span><span>{money(sale.base)}đ</span></div>}
              {sale && sale.source === 'promo' && <div className="flex justify-between text-green-700"><span>{sale.promoLabel || 'Khuyến mãi'}</span><span>{money(sale.price)}đ</span></div>}
              {sale && sale.surcharge > 0 && <div className="flex justify-between text-amber-700"><span>Phụ thu trả góp {sale.surchargePct}%</span><span>+{money(sale.surcharge)}đ</span></div>}
              {salePrice !== price && <div className="flex justify-between text-green-700"><span>Chiết khấu</span><span>-{money(salePrice - price)}đ</span></div>}
              <div className="flex justify-between text-base font-bold"><span>Giá thẻ</span><span>{money(price)}đ</span></div>
              <div className="flex justify-between"><span>Thu ngay</span><b>{money(first)}đ</b></div>
              {price - first > 0 && <div className="flex justify-between text-amber-700"><span>Còn nợ theo lịch</span><b>{money(price - first)}đ</b></div>}
            </div>
          </> : <div className="text-slate-500">Chưa chọn gói</div>}
          {errors.length > 0 && <ul className="list-disc space-y-0.5 pl-4 text-xs text-red-600">{errors.map(e => <li key={e}>{e}</li>)}</ul>}
          <p className="text-xs text-slate-500">Thẻ được lập thành đơn POS để tiền thu vào sổ quỹ ca; các đợt sau thu ở màn Công nợ thẻ, màn POS (khi chọn khách) hoặc thu nợ đơn POS.</p>
        </aside>
      </div>
    </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Thu tiền đợt

/** Thu tiền thẻ tại máy POS — có thể theo yêu cầu CMS gửi sang (request), xác nhận xong báo ngược về CMS */
export function PayModal({ card, request, onClose }: { card: TreatmentCard | null; request?: PayRequest | null; onClose: () => void }) {
  const completePayRequest = useTreatmentStore(s => s.completePayRequest);
  const { userName: cashier } = useTreatmentAuth();
  const cfg = useTreatmentStore(s => s.config);
  const workingBranch = usePosStore(s => s.workingBranch);
  const [amount, setAmount] = useState(0);
  const [tendered, setTendered] = useState(0);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [txnRef, setTxnRef] = useState('');
  const [paid, setPaid] = useState<PaidResult | null>(null);
  const rows = card ? installmentRows(card, cfg) : [];
  const debt = card ? outstanding(card) : 0;
  const posDebt = card?.source === 'pos' ? posDebtOf(card) : Infinity;
  const max = Math.min(debt, posDebt);

  useEffect(() => {
    if (!card) return;
    const nextRow = rows.find(r => r.left > 0);
    const a = Math.min(max, request?.amount || nextRow?.left || debt);
    setAmount(a); setTendered(a); setMethod('cash'); setTxnRef('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  const valid = amount > 0 && amount <= max && methodValid(method, txnRef) && (method !== 'cash' || tendered >= amount);
  const submit = () => {
    if (!card || !valid) return;
    const { change, paymentId } = payCard(card, { amount, method, txnRef, tendered, branchId: workingBranch });
    message.success(`Đã thu ${money(amount)}đ${change ? ` – thối ${money(change)}đ` : ''}`);
    onClose();
    if (paymentId) setPaid({ cardId: card.id, paymentId, change, tendered: method === 'cash' ? tendered : 0 });
    // Xác nhận yêu cầu thu tiền của CMS → CMS nhận thông báo đã thu
    if (request && paymentId) completePayRequest(request.id, { paymentId, amountPaid: amount, method, txnRef: txnRef.trim().toUpperCase(), change, tendered: method === 'cash' ? tendered : 0, by: cashier });
  };

  return (
    <>
    <PaidModal result={paid} onClose={() => setPaid(null)} />
    <Modal open={!!card} onCancel={onClose} title={card ? `Thu tiền thẻ ${card.id}` : ''} centered destroyOnHidden width={560}
      okText={`Thu ${money(amount)}đ`} cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: !valid }}>
      {card && (
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-[#ecf6f9] p-3">
            <div><b>{card.customerName}</b> · {card.packageName}</div>
            <div>Giá thẻ {money(card.price)}đ · Còn nợ <b className="text-red-600">{money(debt)}đ</b></div>
          </div>
          {request && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
              Yêu cầu <b className="font-mono">{request.id}</b> từ CMS ({request.createdBy}, {dayjs(request.createdAt).format('HH:mm')}): thu <b>{money(request.amount)}đ</b> – {request.installmentLabel}
              {request.note && <div className="text-xs">Ghi chú: {request.note}</div>}
            </div>
          )}
          <table className="w-full text-sm">
            <tbody>
              {rows.map(r => (
                <tr key={r.no} className="border-b border-slate-100">
                  <td className="py-1">Đợt {r.no}</td>
                  <td className="py-1">{fmtDate(r.dueDate)}</td>
                  <td className="py-1 text-right">{money(r.amount)}</td>
                  <td className={`py-1 text-right text-xs ${r.state === 'overdue' ? 'text-red-600' : r.state === 'paid' ? 'text-green-600' : 'text-slate-500'}`}>
                    {r.state === 'paid' ? 'Đã thu' : r.state === 'overdue' ? `Thiếu ${money(r.left)} · trễ ${r.overdueDays} ngày` : `Thiếu ${money(r.left)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Field label="Số tiền thu" required hint={card.source === 'pos' && posDebt < debt ? `Đơn POS ${card.orderId} chỉ còn nợ ${money(posDebt)}đ` : 'Tiền thu tự phân bổ vào đợt sớm nhất còn thiếu'}>
            <MoneyInput value={amount} onChange={v => { setAmount(v); if (tendered < v) setTendered(v); }} max={max} />
          </Field>
          <MethodPicker method={method} txnRef={txnRef} onChange={(m, r) => { setMethod(m); setTxnRef(r); }} />
          {method === 'cash' && (
            <Field label="Khách đưa" hint={tendered > amount ? `Tiền thối ${money(tendered - amount)}đ` : undefined}>
              <MoneyInput value={tendered} onChange={setTendered} invalid={tendered < amount} />
            </Field>
          )}
          {card.source === 'pos' && <p className="text-xs text-slate-500">Thẻ bán qua POS: khoản thu được ghi là thu nợ đơn {card.orderId} để khớp sổ quỹ ca.</p>}
        </div>
      )}
    </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Trừ buổi / check-in

interface DeductProps { card: TreatmentCard | null; booking?: CardBooking | null; onClose: () => void; onDone?: () => void }

export function DeductModal({ card, booking, onClose, onDone }: DeductProps) {
  const cfg = useTreatmentStore(s => s.config);
  const deduct = useTreatmentStore(s => s.deduct);
  const workingBranch = usePosStore(s => s.workingBranch);
  const managers = usePosStore(s => s.managers);
  const { can, userName } = useTreatmentAuth();
  const [qty, setQty] = useState<Record<string, number>>({});
  const [branchId, setBranchId] = useState(workingBranch);
  const [staffId, setStaffId] = useState('');
  const [note, setNote] = useState('');
  // Ký ngay trên máy · ký gộp một lần khi khách ra về (Hoàn tất lần đến) · ký phiếu giấy · mã xác nhận 4 số dùng một lần (họp 30/10)
  const [signMode, setSignMode] = useState<'now' | 'checkout' | 'paper' | 'code'>('now');
  const [code, setCode] = useState(() => String(1000 + Math.floor(Math.random() * 9000)));
  const [codeIn, setCodeIn] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const signed = signMode !== 'checkout';
  const [approve, setApprove] = useState(false);
  const [managerId, setManagerId] = useState('');
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (!card) return;
    setQty(booking ? { [booking.serviceId]: 1 } : {});
    setBranchId(booking?.branchId || workingBranch);
    setStaffId(booking?.staffId || '');
    setNote(''); setSignature(null); setSignMode('now'); setCode(String(1000 + Math.floor(Math.random() * 9000))); setCodeIn(''); setApprove(false); setManagerId(''); setPin(''); setReason(''); setPinError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, booking?.id]);

  const req: UsageRequestItem[] = Object.entries(qty).filter(([, q]) => q > 0).map(([serviceId, q]) => ({ serviceId, qty: q }));
  const check = card ? checkUsage(card, req, cfg) : { errors: [], holds: [], value: 0 };
  const lines = card ? lineBalances(card) : [];
  const bal = card ? cardBalance(card) : null;
  const valueServices = card?.kind === 'value' ? TREATMENT_SERVICES.filter(s => !card.allowedServiceIds.length || card.allowedServiceIds.includes(s.id)) : [];
  const needApproval = check.holds.length > 0;
  const approvalOk = !needApproval || (reason.trim().length >= 5 && (can('approve') ? approve : !!managerId && /^\d{6}$/.test(pin)));
  const valid = check.errors.length === 0 && req.length > 0 && !!staffId && (signMode !== 'now' || !!signature) && (signMode !== 'code' || codeIn === code) && approvalOk;

  const submit = () => {
    if (!card || !valid) return;
    let overrideBy: string | undefined;
    if (needApproval) {
      if (can('approve')) overrideBy = userName;
      else {
        const m = managers.find(x => x.id === managerId);
        if (!m || m.pin !== pin) { setPinError('Mã PIN không đúng'); setPin(''); return; }
        overrideBy = m.name;
      }
    }
    const u = deduct(card.id, { items: req, branchId, staffId, note, signed, bookingId: booking?.id, overrideBy, overrideReason: needApproval ? reason.trim() : undefined, by: userName });
    if (u) {
      message.success(`Đã trừ ${u.items.map(i => `${i.name} ×${i.qty}`).join(', ')}`);
      // Chữ ký trên máy → lưu văn bản "Xác nhận buổi thực hiện" vào hồ sơ khách
      // Chữ ký trên máy → phiếu xác nhận lần đến hôm nay (gồm cả các dịch vụ khác khách đã làm trong ngày chưa ký)
      if (signMode === 'now' && signature) {
        signVisit({ posIds: [card.customerId], date: todayYmd(), party: { name: card.customerName, phone: card.customerPhone }, signature, witness: userName, branchId })
          .catch(() => message.warning('Đã trừ buổi nhưng chưa lưu được chữ ký – cho khách ký lại ở bước Hoàn tất lần đến'));
      } else if (signMode === 'checkout') message.info('Nhớ cho khách ký xác nhận khi ra về (nút "Hoàn tất lần đến")', 5);
      else if (signMode === 'code') message.info(`Khách đã xác nhận bằng mã ${code} — mã hết hiệu lực`, 4);
    }
    onClose();
    onDone?.();
  };

  const step = (serviceId: string, d: number, max: number) => setQty(q => ({ ...q, [serviceId]: Math.max(0, Math.min(max, (q[serviceId] || 0) + d)) }));

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Trừ buổi – ${card.customerName}` : ''} centered destroyOnHidden width={620}
      okText="Xác nhận trừ" cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: !valid }}>
      {card && bal && (
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-[#ecf6f9] p-3">
            <div className="font-semibold">{card.packageName} <span className="font-mono text-xs text-slate-500">{card.id}</span></div>
            {awaitingActivation(card) && <div className="my-1 text-xs font-semibold text-amber-700">Lần trừ này kích hoạt thẻ – hạn dùng {card.validityMonths} tháng, đến {fmtDate(addMonthsYmd(ymd(), card.validityMonths || 0))}</div>}
            <div className="text-xs text-slate-600">Hạn dùng {expiryText(card)} · {card.kind === 'session' ? `còn ${bal.remainingSessions}/${bal.totalSessions} buổi` : `số dư ${money(bal.remainingValue)}đ`}</div>
            {booking && <div className="mt-1 text-xs text-[#2c6b7f]">Check-in lịch hẹn {booking.id} – {booking.name} lúc {dayjs(booking.at).format('HH:mm DD/MM')}</div>}
          </div>

          {card.kind === 'session' ? (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {lines.map(l => {
                const q = qty[l.serviceId] || 0;
                const max = Math.max(0, l.available);
                return (
                  <div key={l.serviceId} className={`flex items-center justify-between gap-3 px-3 py-2 ${max === 0 ? 'opacity-50' : ''}`}>
                    <div className="min-w-0">
                      <div className="font-medium">{l.name} {l.role === 'bonus' && <span className="text-xs text-green-600">(tặng)</span>}</div>
                      <div className="text-xs text-slate-500">Còn {max} / {l.total + l.bonus - l.refunded} buổi{l.booked ? ` · ${l.booked} buổi đã hẹn` : ''}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" disabled={q <= 0} onClick={() => step(l.serviceId, -1, max)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 disabled:opacity-40" aria-label="Bớt"><Minus className="h-4 w-4" /></button>
                      <span className="w-8 text-center text-base font-bold">{q}</span>
                      <button type="button" disabled={q >= max} onClick={() => step(l.serviceId, 1, max)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 disabled:opacity-40" aria-label="Thêm"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              <Select mode="multiple" className="w-full" placeholder="Chọn dịch vụ khách làm" value={Object.keys(qty)}
                onChange={(ids: string[]) => setQty(Object.fromEntries(ids.map(id => [id, qty[id] || 1])))}
                options={valueServices.map(s => ({ value: s.id, label: `${s.name} · ${money(s.price)}đ` }))} />
              {Object.keys(qty).map(id => (
                <div key={id} className="flex items-center justify-between gap-2">
                  <span>{serviceOf(id)?.name}</span>
                  <span className="flex items-center gap-2"><InputNumber size="small" min={1} max={20} value={qty[id]} onChange={v => setQty(q => ({ ...q, [id]: Number(v) || 1 }))} /><b className="w-24 text-right">{money((serviceOf(id)?.price || 0) * qty[id])}đ</b></span>
                </div>
              ))}
              {check.value > 0 && <div className="text-right">Trừ thẻ <b className="text-[#2c6b7f]">{money(check.value)}đ</b> · còn lại {money(Math.max(0, bal.remainingValue - check.value))}đ</div>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Chi nhánh thực hiện"><Select className="w-full" value={branchId} onChange={v => { setBranchId(v); setStaffId(''); }} options={branchOptions} /></Field>
            <Field label="Kỹ thuật viên" required><Select className="w-full" value={staffId || undefined} onChange={setStaffId} options={staffOptions(branchId)} placeholder="Chọn KTV" /></Field>
          </div>
          <Input.TextArea rows={2} maxLength={300} value={note} onChange={e => setNote(e.target.value)} placeholder="Ghi chú buổi làm (tình trạng da, phản ứng sau làm...)" />

          {check.errors.length > 0 && <Alert type="error" showIcon message={<ul className="list-disc pl-4">{check.errors.map(e => <li key={e}>{e}</li>)}</ul>} />}
          {needApproval && check.errors.length === 0 && (
            <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center gap-1.5 font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Cần quản lý duyệt</div>
              <ul className="list-disc pl-5 text-amber-800">{check.holds.map(h => <li key={h}>{h}</li>)}</ul>
              {can('approve')
                ? <Checkbox checked={approve} onChange={e => setApprove(e.target.checked)}><span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Tôi duyệt cho khách sử dụng</span></Checkbox>
                : (
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={managerId || undefined} onChange={setManagerId} placeholder="Quản lý duyệt" options={managers.map(m => ({ value: m.id, label: m.name }))} />
                    <Input.Password prefix={<KeyRound className="h-4 w-4 text-slate-400" />} maxLength={6} value={pin} placeholder="PIN 6 số" status={pinError ? 'error' : undefined}
                      onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setPinError(''); }} />
                    {pinError && <span className="col-span-2 text-xs text-red-600">{pinError}</span>}
                  </div>
                )}
              <Input value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="Lý do duyệt (tối thiểu 5 ký tự), VD: khách hẹn đóng đợt 2 vào thứ Sáu" />
            </div>
          )}
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-slate-700">Khách ký xác nhận <span className="text-red-500">*</span></span>
              <Segmented size="small" value={signMode} onChange={v => setSignMode(v as typeof signMode)}
                options={[{ value: 'now', label: 'Ký ngay' }, { value: 'code', label: 'Mã xác nhận' }, { value: 'checkout', label: 'Ký khi ra về' }, { value: 'paper', label: 'Ký giấy' }]} />
            </div>
            {signMode === 'now' && <SignaturePad height={130} onChange={setSignature} placeholder="Đưa máy cho khách ký vào đây" />}
            {signMode === 'checkout' && <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Khách còn làm thêm dịch vụ: ký gộp một lần cho cả lần đến ở bước <b>Hoàn tất lần đến</b>. Lần đến sẽ ở trạng thái "Chờ ký" đến khi khách ký.</div>}
            {signMode === 'code' && (
              <div className="rounded-lg border border-[#357f96]/30 bg-[#357f96]/5 px-3 py-2.5 text-xs text-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>Mã xác nhận buổi này (dùng một lần):</span>
                  <span className="font-mono text-2xl font-bold tracking-[0.3em] text-[#357f96]">{code}</span>
                </div>
                <div className="mt-1 text-slate-500">Gửi mã cho khách (SMS / Zalo / app khách quét QR — chờ nối API). Khách đọc lại hoặc nhập mã để xác nhận đúng dịch vụ, đúng số buổi.</div>
                <Input className="mt-2" inputMode="numeric" maxLength={4} value={codeIn} onChange={e => setCodeIn(e.target.value.replace(/\D/g, ''))} placeholder="Mã khách nhập / đọc lại"
                  status={codeIn.length === 4 && codeIn !== code ? 'error' : undefined} />
                {codeIn.length === 4 && codeIn !== code && <div className="mt-1 text-red-500">Mã không đúng</div>}
              </div>
            )}
            {signMode === 'paper' && <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">Khách ký trên phiếu giấy – lưu phiếu tại quầy (có thể chụp phiếu đính kèm ở Hồ sơ 360 › Lần đến).</div>}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Đặt lịch từ thẻ

export function BookingModal({ card, onClose }: { card: TreatmentCard | null; onClose: () => void }) {
  const createBooking = useTreatmentStore(s => s.createBooking);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { userName } = useTreatmentAuth();
  const [serviceId, setServiceId] = useState('');
  const [at, setAt] = useState<Dayjs | null>(null);
  const [branchId, setBranchId] = useState(workingBranch);
  const [staffId, setStaffId] = useState('');
  const [note, setNote] = useState('');

  const lines = card ? lineBalances(card) : [];
  const options = card?.kind === 'session'
    ? lines.map(l => ({ value: l.serviceId, label: `${l.name} – còn ${Math.max(0, l.available - l.booked)} buổi chưa hẹn`, disabled: l.available - l.booked <= 0 }))
    : TREATMENT_SERVICES.filter(s => !card?.allowedServiceIds.length || card.allowedServiceIds.includes(s.id)).map(s => ({ value: s.id, label: `${s.name} · ${money(s.price)}đ` }));

  useEffect(() => {
    if (!card) return;
    setServiceId(options.find(o => !('disabled' in o && o.disabled))?.value || '');
    setAt(dayjs().add(1, 'day').hour(10).minute(0).second(0));
    setBranchId(workingBranch); setStaffId(''); setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  const errors: string[] = [];
  if (!serviceId) errors.push('Chọn dịch vụ');
  if (!at) errors.push('Chọn thời gian');
  else if (at.isBefore(dayjs())) errors.push('Thời gian hẹn đã qua');
  else if (card && at.format('YYYY-MM-DD') > card.expiresAt) errors.push(`Thẻ hết hạn ngày ${fmtDate(card.expiresAt)} – gia hạn trước khi hẹn sau ngày này`);

  const submit = () => {
    if (!card || errors.length || !at) return;
    const b = createBooking(card.id, { at: at.toISOString(), branchId, serviceId, staffId, note: note.trim(), by: userName });
    message.success(`Đã đặt lịch ${b.name} lúc ${at.format('HH:mm DD/MM/YYYY')}`);
    onClose();
  };

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Đặt lịch từ thẻ ${card.id}` : ''} centered destroyOnHidden width={520}
      okText="Đặt lịch" cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: errors.length > 0 }}>
      {card && (
        <div className="space-y-3 text-sm">
          <div className="text-slate-600"><b>{card.customerName}</b> · {card.packageName}</div>
          <Field label="Dịch vụ" required><Select className="w-full" value={serviceId || undefined} onChange={setServiceId} options={options} /></Field>
          <Field label="Thời gian" required>
            <DatePicker className="w-full" showTime={{ format: 'HH:mm', minuteStep: 15 }} format="DD/MM/YYYY HH:mm" value={at} onChange={setAt}
              disabledDate={d => d.isBefore(dayjs(), 'day') || d.format('YYYY-MM-DD') > card.expiresAt} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Chi nhánh"><Select className="w-full" value={branchId} onChange={v => { setBranchId(v); setStaffId(''); }} options={branchOptions} /></Field>
            <Field label="KTV (nếu khách chọn)"><Select className="w-full" allowClear value={staffId || undefined} onChange={v => setStaffId(v || '')} options={staffOptions(branchId)} placeholder="Bất kỳ" /></Field>
          </div>
          <Input.TextArea rows={2} maxLength={200} value={note} onChange={e => setNote(e.target.value)} placeholder="Ghi chú cho lễ tân / KTV" />
          {errors.length > 0 && serviceId && at && <div className="text-xs text-red-600">{errors.join(' · ')}</div>}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Gia hạn

export function ExtendModal({ card, onClose }: { card: TreatmentCard | null; onClose: () => void }) {
  const extendCard = useTreatmentStore(s => s.extendCard);
  const pkg = useTreatmentStore(s => s.packages.find(p => p.id === card?.packageId));
  const { can, userName } = useTreatmentAuth();
  const [date, setDate] = useState<Dayjs | null>(null);
  const [fee, setFee] = useState(0);
  const [feeEdited, setFeeEdited] = useState(false);
  const [reason, setReason] = useState('');
  const base = card ? (card.expiresAt < ymd() ? ymd() : card.expiresAt) : ymd();
  const months = date ? monthsBetween(base, date.format('YYYY-MM-DD')) : 0;
  const quote = card ? extensionQuote(card, months, pkg) : null;
  const policy = card ? cardPolicy(card, pkg) : null;

  useEffect(() => {
    if (!card) return;
    setDate(dayjs(addMonthsYmd(base, 1))); setFeeEdited(false); setReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);
  // Phí theo quy định gói, tự tính lại khi đổi hạn mới (trừ khi quản lý đã sửa tay)
  useEffect(() => { if (quote && !feeEdited) setFee(quote.fee); }, [quote?.fee, feeEdited]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vượt số tháng gia hạn tối đa của gói: chỉ quản lý được duyệt ngoại lệ
  const overLimit = !!quote && !quote.allowed && months > 0;
  const valid = !!date && date.format('YYYY-MM-DD') > (card?.expiresAt || '') && reason.trim().length >= 5 && (!overLimit || can('approve'))
    && (can('approve') || fee >= (quote?.fee || 0));
  const submit = () => {
    if (!card || !valid || !date) return;
    extendCard(card.id, { expiresAt: date.format('YYYY-MM-DD'), months, fee, reason: reason.trim(), by: userName });
    message.success(`Đã gia hạn thẻ đến ${date.format('DD/MM/YYYY')}${fee ? ` – phí ${money(fee)}đ` : ''}`);
    onClose();
  };

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Gia hạn thẻ ${card.id}` : ''} centered destroyOnHidden width={500}
      okText="Gia hạn" cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: !valid }}>
      {card && quote && policy && (
        <div className="space-y-3 text-sm">
          <div>Hạn hiện tại: <b className={card.expiresAt < ymd() ? 'text-red-600' : ''}>{fmtDate(card.expiresAt)}</b></div>
          {awaitingActivation(card) && <Alert type="info" showIcon message="Thẻ chưa kích hoạt: gia hạn là dời hạn phải dùng buổi đầu. Thời hạn dùng thẻ vẫn tính từ buổi đầu." />}
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            Quy định gói: {policy.maxExtensionMonths
              ? <>gia hạn tối đa <b>{policy.maxExtensionMonths} tháng</b>, phí <b>{money(policy.extensionFeePerMonth)}đ/tháng</b></>
              : <b>không cho gia hạn</b>} · đã gia hạn {quote.used} tháng, còn {quote.left} tháng
          </div>
          <div className="flex flex-wrap gap-2">
            {[1, 3, 6].map(m => (
              <button key={m} type="button" onClick={() => setDate(dayjs(addMonthsYmd(base, m)))} className="rounded-lg border border-slate-300 px-3 py-1 text-sm hover:border-[#3e91ab]">+{m} tháng</button>
            ))}
          </div>
          <Field label="Hạn mới" required hint={months ? `Gia hạn ${months} tháng` : undefined}>
            <DatePicker className="w-full" format="DD/MM/YYYY" allowClear={false} value={date} onChange={setDate} disabledDate={d => d.format('YYYY-MM-DD') <= card.expiresAt} />
          </Field>
          {overLimit && (
            <Alert type={can('approve') ? 'warning' : 'error'} showIcon
              message={can('approve') ? `Vượt số tháng gia hạn tối đa của gói (còn ${quote.left} tháng) – bạn đang duyệt ngoại lệ.` : `Gói chỉ còn được gia hạn ${quote.left} tháng – cần quản lý duyệt.`} />
          )}
          <Field label="Phí gia hạn" hint={`Theo quy định: ${money(quote.fee)}đ${can('approve') ? ' – quản lý được giảm / miễn phí' : ''}. Thu phí tại POS như một dịch vụ.`}>
            <MoneyInput value={fee} onChange={v => { setFee(v); setFeeEdited(true); }} invalid={!can('approve') && fee < quote.fee} />
          </Field>
          <Field label="Lý do" required><Input.TextArea rows={2} maxLength={200} value={reason} onChange={e => setReason(e.target.value)} placeholder="VD: khách đi công tác 2 tháng, xin bảo lưu" /></Field>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Điều chỉnh buổi / số dư

export function AdjustModal({ card, onClose }: { card: TreatmentCard | null; onClose: () => void }) {
  const adjustCard = useTreatmentStore(s => s.adjustCard);
  const { userName } = useTreatmentAuth();
  const [serviceId, setServiceId] = useState('');
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState('');
  const lines = card ? lineBalances(card) : [];
  const bal = card ? cardBalance(card) : null;

  useEffect(() => {
    if (!card) return;
    setServiceId(card.lines[0]?.serviceId || ''); setDelta(card.kind === 'value' ? 0 : 1); setReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  const line = lines.find(l => l.serviceId === serviceId);
  const minDelta = card?.kind === 'value' ? -(bal?.remainingValue || 0) : -(line ? Math.max(0, line.available) : 0);
  const valid = delta !== 0 && delta >= minDelta && reason.trim().length >= 5 && (card?.kind === 'value' || !!serviceId);
  const submit = () => {
    if (!card || !valid) return;
    adjustCard(card.id, { serviceId, delta, reason: reason.trim(), by: userName });
    message.success('Đã điều chỉnh thẻ');
    onClose();
  };

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Điều chỉnh thẻ ${card.id}` : ''} centered destroyOnHidden width={500}
      okText="Lưu điều chỉnh" cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: !valid }}>
      {card && (
        <div className="space-y-3 text-sm">
          {card.kind === 'session' ? <>
            <Field label="Dịch vụ" hint="Chọn dịch vụ ngoài thẻ để tặng thêm dịch vụ mới">
              <Select className="w-full" showSearch optionFilterProp="label" value={serviceId || undefined} onChange={setServiceId}
                options={[
                  { label: 'Trong thẻ', options: lines.map(l => ({ value: l.serviceId, label: `${l.name} – còn ${l.available}` })) },
                  { label: 'Tặng dịch vụ khác', options: TREATMENT_SERVICES.filter(s => !lines.some(l => l.serviceId === s.id)).map(s => ({ value: s.id, label: s.name })) },
                ]} />
            </Field>
            <Field label="Số buổi (+ tặng thêm, − giảm)" hint="Buổi tặng thêm không tính tiền khi hoàn">
              <InputNumber className="w-full" min={minDelta} max={50} value={delta} onChange={v => setDelta(Number(v) || 0)} />
            </Field>
          </> : (
            <Field label="Điều chỉnh số dư (đ)" hint={`Số dư hiện tại ${money(bal?.remainingValue || 0)}đ – nhập số âm để giảm`}>
              <InputNumber className="w-full" min={minDelta} step={100_000} value={delta} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                parser={v => Number((v || '').replace(/\./g, ''))} onChange={v => setDelta(Number(v) || 0)} />
            </Field>
          )}
          <Field label="Lý do" required><Input.TextArea rows={2} maxLength={200} value={reason} onChange={e => setReason(e.target.value)} placeholder="VD: bù buổi do máy hỏng / tặng khách VIP / trừ sai buổi" /></Field>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Hoàn tiền

export function RefundModal({ card, onClose }: { card: TreatmentCard | null; onClose: () => void }) {
  const cfg = useTreatmentStore(s => s.config);
  const pkg = useTreatmentStore(s => s.packages.find(p => p.id === card?.packageId));
  const { can } = useTreatmentAuth();
  const policy = card ? cardPolicy(card, pkg) : null;
  const minFee = can('approve') ? 0 : policy?.refundFeePct || 0;
  const [mode, setMode] = useState<'partial' | 'full'>('partial');
  const [sessions, setSessions] = useState<Record<string, number>>({});
  const [valueReturned, setValueReturned] = useState(0);
  const [feePct, setFeePct] = useState(cfg.refundFeePct);
  const [method, setMethod] = useState<PaymentMethod>('transfer');
  const [txnRef, setTxnRef] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!card) return;
    setMode('partial'); setSessions({}); setValueReturned(0); setFeePct(policy?.refundFeePct ?? cfg.refundFeePct); setMethod('transfer'); setTxnRef(''); setReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  const lines = card ? lineBalances(card).filter(l => l.role === 'main') : [];
  const bal = card ? cardBalance(card) : null;
  const sessionList = Object.entries(sessions).filter(([, q]) => q > 0).map(([serviceId, qty]) => ({ serviceId, qty }));
  const q = card ? refundQuote(card, { mode, sessions: sessionList, valueReturned, feePct }) : null;
  const needApproval = card?.source === 'pos' || (!can('approve') && (q?.amount || 0) >= cfg.refundApprovalThreshold);
  const blocked = !!policy && !policy.allowRefund && !can('approve');
  const valid = !blocked && !!q && (mode === 'full' || sessionList.length > 0 || valueReturned > 0) && reason.trim().length >= 5 && methodValid(method, txnRef)
    && (mode === 'full' || q.amount > 0) && feePct >= minFee;

  const submit = () => {
    if (!card || !q || !valid) return;
    try {
      const r = refundCard(card, {
        mode, sessions: mode === 'partial' && card.kind === 'session' ? sessionList : [], valueReturned: mode === 'partial' && card.kind === 'value' ? valueReturned : 0,
        amount: q.amount, fee: q.fee, method, txnRef, reason: reason.trim(),
      }, can('approve'));
      message.success(r.status === 'paid' ? `Đã hoàn ${money(r.amount)}đ` : `Đã tạo phiếu hoàn ${money(r.amount)}đ – chờ quản lý duyệt`);
      onClose();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  const row = (label: string, value: React.ReactNode, cls = '') => <div className={`flex justify-between ${cls}`}><span>{label}</span><span>{value}</span></div>;

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Hoàn tiền thẻ ${card.id}` : ''} centered destroyOnHidden width={600}
      okText={needApproval && card?.source !== 'pos' ? 'Gửi duyệt' : 'Tạo phiếu hoàn'} okButtonProps={{ danger: true, disabled: !valid }} cancelText="Hủy" onOk={submit}>
      {card && q && bal && (
        <div className="space-y-3 text-sm">
          <Radio.Group value={mode} onChange={e => setMode(e.target.value)} optionType="button" buttonStyle="solid"
            options={[{ value: 'partial', label: card.kind === 'session' ? 'Hoàn một phần (trả lại buổi)' : 'Hoàn một phần số dư' }, { value: 'full', label: 'Hoàn toàn bộ & đóng thẻ' }]} />
          {mode === 'partial' && card.kind === 'session' && (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {lines.map(l => (
                <div key={l.serviceId} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span>{l.name} <span className="text-xs text-slate-500">– còn {l.available} buổi · {money(l.unitValue)}đ/buổi</span></span>
                  <InputNumber size="small" min={0} max={Math.max(0, Math.min(l.available, l.total - Math.min(l.used, l.total) - l.refunded))} value={sessions[l.serviceId] || 0}
                    onChange={v => setSessions(s => ({ ...s, [l.serviceId]: Number(v) || 0 }))} />
                </div>
              ))}
              {lines.length === 0 && <div className="px-3 py-2 text-slate-500">Thẻ không có dịch vụ trả tiền</div>}
            </div>
          )}
          {mode === 'partial' && card.kind === 'value' && (
            <Field label="Mệnh giá trả lại" hint={`Số dư còn ${money(bal.remainingValue)}đ`}><MoneyInput value={valueReturned} onChange={setValueReturned} max={bal.remainingValue} /></Field>
          )}
          <div className="space-y-1 rounded-lg bg-slate-50 p-3">
            {row('Khách đã đóng', `${money(q.paid)}đ`)}
            {row('Giá trị đã sử dụng', `-${money(q.consumed)}đ`)}
            {q.already > 0 && row('Đã hoàn / đang chờ hoàn', `-${money(q.already)}đ`)}
            {row('Tối đa còn được hoàn', <b>{money(q.cap)}đ</b>)}
            {mode === 'partial' && row('Giá trị phần trả lại', `${money(q.gross)}đ`)}
            <div className="flex items-center justify-between">
              <span>Công ty giữ lại (% phần hoàn)</span>
              <span className="flex items-center gap-2"><InputNumber size="small" min={minFee} max={100} value={feePct} onChange={v => setFeePct(Math.max(minFee, Number(v) || 0))} addonAfter="%" className="w-24" /> -{money(q.fee)}đ</span>
            </div>
            {row(`Khách nhận lại (${100 - feePct}% phần hoàn)`, <b className="text-lg text-violet-700">{money(q.amount)}đ</b>, 'border-t border-slate-200 pt-1')}
            {mode === 'full' && outstanding(card) > 0 && <div className="text-xs text-amber-700">Công nợ còn lại {money(outstanding(card))}đ sẽ được xóa khi thẻ đóng.</div>}
          </div>
          <Field label="Phương thức hoàn"><MethodPicker method={method} txnRef={txnRef} onChange={(m, r) => { setMethod(m); setTxnRef(r); }} /></Field>
          <Field label="Lý do" required><Input.TextArea rows={2} maxLength={300} value={reason} onChange={e => setReason(e.target.value)} placeholder="Tối thiểu 5 ký tự" /></Field>
          {policy && !policy.allowRefund && <Alert type={blocked ? 'error' : 'warning'} showIcon message={blocked ? 'Gói này không cho hoàn tiền theo quy định – cần quản lý xử lý ngoại lệ.' : 'Gói không cho hoàn tiền – bạn đang hoàn ngoại lệ bằng quyền quản lý, ghi rõ lý do.'} />}
          {policy && policy.allowRefund && <p className="text-xs text-slate-500">Quy định gói: giữ lại {policy.refundFeePct}%, khách nhận lại {100 - policy.refundFeePct}% giá trị phần chưa dùng{minFee > 0 ? ' (chỉ quản lý được giảm phí)' : ''}.</p>}
          {card.source === 'pos'
            ? <Alert type="info" showIcon message={`Phiếu hoàn được tạo trên đơn POS ${card.orderId}; quản lý duyệt và thu ngân chi tiền ở màn Hoàn tiền POS.`} />
            : needApproval && <Alert type="warning" showIcon message={`Phiếu từ ${money(cfg.refundApprovalThreshold)}đ cần quản lý duyệt trước khi chi.`} />}
          {mode === 'partial' && card.kind === 'session' && sessionList.length > 0 && <p className="text-xs text-slate-500">Các buổi trả lại bị khóa ngay khi tạo phiếu; nếu phiếu bị từ chối, buổi được mở lại.</p>}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sửa lịch thanh toán

export function PlanModal({ card, onClose }: { card: TreatmentCard | null; onClose: () => void }) {
  const cfg = useTreatmentStore(s => s.config);
  const pkg = useTreatmentStore(s => s.packages.find(p => p.id === card?.packageId));
  const setPlanStore = useTreatmentStore(s => s.setPlan);
  const { userName } = useTreatmentAuth();
  const [plan, setPlan] = useState<Installment[]>([]);
  const [reason, setReason] = useState('');
  const rows = card ? installmentRows(card, cfg) : [];
  const lockedNos = rows.filter(r => r.state === 'paid').map(r => r.no);

  useEffect(() => {
    if (!card) return;
    setPlan(card.installments.map(i => ({ ...i }))); setReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);

  const total = plan.reduce((s, p) => s + p.amount, 0);
  const valid = !!card && total === card.price && plan.every(p => p.amount > 0) && reason.trim().length >= 5;
  const addRow = () => {
    const last = plan[plan.length - 1];
    setPlan([...plan, { no: plan.length + 1, dueDate: addDaysYmd(last?.dueDate || ymd(), pkg ? pricingOf(pkg).intervalDays : cfg.defaultIntervalDays), amount: 0 }]);
  };
  const submit = () => {
    if (!card || !valid) return;
    setPlanStore(card.id, [...plan].sort((a, b) => a.dueDate.localeCompare(b.dueDate)).map((p, i) => ({ ...p, no: i + 1 })), userName, reason.trim());
    message.success('Đã cập nhật lịch thanh toán');
    onClose();
  };

  return (
    <Modal open={!!card} onCancel={onClose} title={card ? `Lịch thanh toán thẻ ${card.id}` : ''} centered destroyOnHidden width={560}
      okText="Lưu lịch" cancelText="Hủy" onOk={submit} okButtonProps={{ disabled: !valid }}>
      {card && (
        <div className="space-y-3 text-sm">
          <PlanEditor plan={plan} onChange={setPlan} lockedNos={lockedNos} />
          <div className="flex items-center justify-between">
            <button type="button" onClick={addRow} className="flex items-center gap-1 text-sm font-semibold text-[#2c6b7f]"><Plus className="h-4 w-4" /> Thêm đợt</button>
            <span className={total === card.price ? 'text-slate-600' : 'font-semibold text-red-600'}>Tổng {money(total)} / {money(card.price)}đ</span>
          </div>
          <Field label="Lý do" required><Input value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="VD: khách xin dời đợt 3 sang cuối tháng" /></Field>
        </div>
      )}
    </Modal>
  );
}

/** Tóm tắt gói để hiển thị trong bảng / danh sách */
export const packageSummary = (p: TreatmentPackage) => (p.kind === 'value'
  ? `Mệnh giá ${moneyShort(p.faceValue)}`
  : p.items.map(i => `${serviceOf(i.serviceId)?.name} ×${i.sessions}${i.role === 'bonus' ? ` (${ROLE_LABEL.bonus.toLowerCase()})` : ''}`).join(' · '));
